import { ActionMenu, Alert, Badge, EmptyState, IconButton, Skeleton } from '@ntconsult/aluma-react';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Bell, BellPlus, CheckCircle2, Clock } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Notificacao, NotificationKind } from '@/types/api';
import { useMarcarLida, useNotificacoes } from '../hooks';

const KIND_ICON: Record<NotificationKind, typeof Bell> = {
  atribuido: BellPlus,
  prazo_7d: Clock,
  prazo_1d: Clock,
  concluido: CheckCircle2,
};

export function NotificationBell() {
  const { t } = useTranslation(['notificacoes', 'common']);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError } = useNotificacoes();
  const marcarLida = useMarcarLida();

  const naoLidas = data?.filter((n) => n.readAt === null).length ?? 0;
  const badgeText = naoLidas > 9 ? '9+' : String(naoLidas);

  function handleClick(n: Notificacao) {
    if (n.readAt === null) marcarLida.mutate(n.id);
    const base = n.payload.alvoTipo === 'trilha' ? '/trilhas' : '/cursos';
    navigate(`${base}/${n.payload.alvoId}`);
  }

  return (
    <ActionMenu
      open={open}
      onOpenChange={setOpen}
      align="right"
      width="lg"
      trigger={
        <IconButton
          label={t('notificacoes:bell')}
          variant="ghost"
          icon={
            <span className="relative inline-flex">
              <Bell aria-hidden />
              {naoLidas > 0 && (
                <span className="absolute -right-2 -top-2">
                  <Badge tone="danger" variant="solid" size="sm" shape="pill">
                    {badgeText}
                  </Badge>
                </span>
              )}
            </span>
          }
        />
      }
    >
      <div className="flex items-center justify-between px-3 py-2">
        <span className="text-sm font-medium">{t('notificacoes:title')}</span>
        {naoLidas > 0 && (
          <Badge tone="danger" variant="soft" size="sm">
            {badgeText}
          </Badge>
        )}
      </div>
      <ActionMenu.Separator />

      {isLoading && (
        <div className="flex flex-col gap-2 px-3 py-2">
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-10 w-72" />
          <Skeleton className="h-10 w-72" />
        </div>
      )}

      {isError && (
        <div className="px-3 py-2">
          <Alert variant="danger" title={t('notificacoes:error')} />
        </div>
      )}

      {!isLoading && !isError && data && data.length === 0 && (
        <div className="px-3 py-4">
          <EmptyState title={t('notificacoes:empty')} />
        </div>
      )}

      {!isLoading &&
        !isError &&
        data &&
        data.length > 0 &&
        data.map((n, i) => {
          const Icon = KIND_ICON[n.kind];
          const lida = n.readAt !== null;
          return (
            <Fragment key={n.id}>
              {i > 0 && <ActionMenu.Separator />}
              <ActionMenu.Item
                icon={
                  <span className="relative inline-flex">
                    <Icon aria-hidden />
                    {!lida && (
                      <span
                        aria-hidden
                        className="absolute -left-1 -top-1 h-2 w-2 rounded-full bg-danger"
                      />
                    )}
                  </span>
                }
                onClick={() => handleClick(n)}
              >
                <span className="flex flex-col gap-0.5 text-left">
                  <span className={lida ? 'text-foreground-muted' : 'font-semibold'}>
                    {t(`notificacoes:kind.${n.kind}`)}
                    {' · '}
                    {n.payload.alvoTitulo}
                  </span>
                  <span className="text-xs text-foreground-subtle">
                    {n.payload.dueDate && (
                      <>
                        {t('notificacoes:vence', {
                          data: format(new Date(n.payload.dueDate), 'dd/MM/yyyy'),
                        })}
                        {' · '}
                      </>
                    )}
                    {formatDistanceToNow(new Date(n.createdAt), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </span>
                </span>
              </ActionMenu.Item>
            </Fragment>
          );
        })}
    </ActionMenu>
  );
}
